import React, { memo, useState, useEffect, useRef } from 'react';

import {
  Col,
  Modal,
  Card,
  CardHeader,
  CardBody,
  Form,
  Row,
  Label,
  FormGroup,
  Input,
  Spinner,
} from 'reactstrap';

import FormButtons from '~/components/FormButtons';

import api from '~/services/api';
import notification from '~/services/notification';

import util from '~/assets/util';

const DEFAULT_STATE = {
  attribute_id: 0,
  name: '',
};

interface AttributeModalProps {
  isOpen?: any;
  id?: any;
  onSave?: any;
  onToggle?: any;
}

function AttributeModal({ isOpen = false, id, onSave, onToggle }: AttributeModalProps) {
  const [loading, setLoading] = useState(!!id);

  const [editing, setEditing] = useState(!!id);
  const [attribute, setAttribute] = useState(DEFAULT_STATE);

  const inputNameRef = useRef(null);

  const handleChange = (ev) => {
    const name = ev.target.name;

    setAttribute({ ...attribute, [name]: ev.target.value });
  };

  const doSave = async (ev) => {
    ev.preventDefault();
    ev.stopPropagation();

    if (loading) {
      return;
    }

    setLoading(true);

    const url = 'admin/products/attributes';

    try {
      const { data } = id
        ? await api.put(`${url}/${id}`, attribute)
        : await api.post(url, attribute);

      notification.$s(util.t('ATTRIBUTE_SAVED'));

      setAttribute(DEFAULT_STATE);

      onSave(data?.attribute);
    } catch (e) {
      console.error('attributeModal.doSave', e);

      notification.$e(e);
    } finally {
      setLoading(false);
    }
  };

  async function loadAttribute(id) {
    try {
      const { data } = await api.get(`admin/attributes/${id}`);

      const attribute = data?.attribute;

      setAttribute(attribute);
    } catch (e) {
      notification.$e(e);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (!editing) {
      inputNameRef.current?.focus();
    }
  }, [editing]);

  useEffect(() => {
    id && loadAttribute(id);
  }, []);

  return (
    <Modal autoFocus={false} isOpen={isOpen} toggle={onToggle}>
      <Card>
        <CardHeader>{util.t('ATTRIBUTE')}</CardHeader>
        <CardBody>
          {loading ? (
            <Spinner color="primary" className="d-block m-auto" />
          ) : (
            <Form onSubmit={doSave}>
              <fieldset disabled={editing}>
                <Row form>
                  <Col>
                    <FormGroup>
                      <Label htmlFor="name">
                        {util.t('NAME')} <strong>*</strong>
                      </Label>

                      <Input
                        autoFocus
                        id="name"
                        name="name"
                        value={attribute.name}
                        onChange={handleChange}
                        innerRef={inputNameRef}
                        max="255"
                        required
                      />
                    </FormGroup>
                  </Col>
                </Row>
              </fieldset>
              <FormButtons
                isEditing={editing}
                isSaving={loading}
                onCancel={onToggle}
                onEdit={() => setEditing(false)}
              />
            </Form>
          )}
        </CardBody>
      </Card>
    </Modal>
  );
}

export default memo(AttributeModal);
